import React, { useState } from 'react';
import { mockAgencyProfile, mockClientWorkspaces, mockPilotChecklist } from '../data/agency';
import { KpiCard } from '../components/ui/KpiCard';

export const Agency = () => {
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [showEligibilityModal, setShowEligibilityModal] = useState(false);

  const handleConnectionDetails = (clientId: string) => {
    setSelectedClient(clientId);
  };

  const closeModal = () => {
    setSelectedClient(null);
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'Connected':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Syncing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Needs Connection':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const clientsCount = mockClientWorkspaces.length;
  const checklistItems = [
    mockPilotChecklist.clientConnected,
    mockPilotChecklist.revenueAtRiskSurfaced,
    mockPilotChecklist.actionsSharedWithClient,
    mockPilotChecklist.weeklyInsightsEnabled,
  ];
  const completedCount = checklistItems.filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 bg-gradient-to-br from-white to-blue-50/30 rounded-xl p-6 border border-gray-200/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img
                src="/logo/logo-glossy.png"
                alt="RiverBlue Logo"
                className="w-16 h-16"
                style={{
                  filter: 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.4)) drop-shadow(0 0 40px rgba(59, 130, 246, 0.3)) brightness(1.1) contrast(1.05) saturate(1.1)',
                }}
              />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Agency Portal</h1>
                <p className="mt-1 text-sm text-gray-600">Pilot status and client workspaces</p>
                <p className="mt-1 text-xs text-gray-500">Manage client access and surface revenue risk without touching their CRM.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white border border-gray-200 text-gray-700 shadow-sm">
                Agency: {mockAgencyProfile.name}
              </span>
              <button
                disabled
                className="px-4 py-2 bg-riverblue-primary/10 text-riverblue-primary rounded-lg border border-riverblue-primary/20 cursor-not-allowed text-sm font-medium opacity-60"
              >
                Add Client Workspace
              </button>
            </div>
          </div>
          
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-riverblue-primary/10 text-riverblue-deep border border-riverblue-primary/20">
              Pilot
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
              Real Estate + Medspas
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
              Read-only OAuth
            </span>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard
            title="Status"
            value={mockAgencyProfile.status}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <KpiCard
            title="CRM"
            value={`${mockAgencyProfile.crm} Connected`}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            }
          />
          <KpiCard
            title="Clients"
            value={clientsCount}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
          />
          <KpiCard
            title="Last Sync"
            value={mockAgencyProfile.lastSync}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            }
          />
        </div>

        {/* Client Workspaces */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Client Workspaces</h2>
            <span className="text-sm text-gray-500">{mockClientWorkspaces.length} total</span>
          </div>
          <div className="space-y-3">
            {mockClientWorkspaces.map((client) => {
              const isDisabled = client.status === 'Needs Connection';
              return (
                <div
                  key={client.id}
                  className="border border-gray-200 rounded-lg p-5 hover:border-riverblue-primary/30 hover:shadow-sm transition-all bg-white"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-base font-bold text-gray-900">{client.name}</h3>
                        <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                          {client.vertical}
                        </span>
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusBadgeColor(client.status)}`}>
                          {client.status}
                        </span>
                      </div>
                      {client.lastSync && (
                        <p className="text-sm text-gray-500">Last sync: {client.lastSync}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {isDisabled ? (
                        <button
                          onClick={() => handleConnectionDetails(client.id)}
                          className="px-4 py-2 bg-riverblue-primary text-white rounded-lg hover:bg-riverblue-deep border border-riverblue-primary shadow-sm hover:shadow transition-colors text-sm font-medium"
                        >
                          Complete Connection
                        </button>
                      ) : (
                        <button
                          className="px-4 py-2 bg-riverblue-primary text-white rounded-lg hover:bg-riverblue-deep border border-riverblue-primary shadow-sm hover:shadow transition-colors text-sm font-medium"
                        >
                          Open Dashboard
                        </button>
                      )}
                      <button
                        onClick={() => handleConnectionDetails(client.id)}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors text-sm font-medium"
                      >
                        Connection Details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pilot Checklist */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Pilot Checklist</h2>
            <span className="text-sm font-medium text-gray-600">
              Pilot Progress: {Math.round((completedCount / checklistItems.length) * 100)}%
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Setup</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  {mockPilotChecklist.clientConnected ? (
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <div className="w-5 h-5 border-2 border-gray-300 rounded mr-3 flex-shrink-0" />
                  )}
                  <span className="text-sm text-gray-700">Client connected</span>
                </div>
                <div className="flex items-center">
                  {mockPilotChecklist.revenueAtRiskSurfaced ? (
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <div className="w-5 h-5 border-2 border-gray-300 rounded mr-3 flex-shrink-0" />
                  )}
                  <span className="text-sm text-gray-700">Revenue at risk surfaced</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Value Delivered</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  {mockPilotChecklist.actionsSharedWithClient ? (
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <div className="w-5 h-5 border-2 border-gray-300 rounded mr-3 flex-shrink-0" />
                  )}
                  <span className="text-sm text-gray-700">Actions shared with client</span>
                </div>
                <div className="flex items-center">
                  {mockPilotChecklist.weeklyInsightsEnabled ? (
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <div className="w-5 h-5 border-2 border-gray-300 rounded mr-3 flex-shrink-0" />
                  )}
                  <span className="text-sm text-gray-700">Weekly insights enabled (optional)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* White-Label Eligibility */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">White-Label Eligibility</h2>
              <div className="space-y-2 mb-4">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Current:</span> Not eligible yet
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Unlock:</span> Unlocks after 1–3 paying client accounts under management.
                </p>
              </div>
            </div>
            <span className="px-3 py-1 text-sm font-medium rounded-full bg-gray-100 text-gray-800 border border-gray-200">
              Not Eligible Yet
            </span>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <button
              disabled
              className="px-4 py-2 bg-gray-200 text-gray-500 rounded-lg cursor-not-allowed text-sm font-medium"
            >
              Request White-Label Eligibility
            </button>
            <button
              onClick={() => setShowEligibilityModal(true)}
              className="text-sm text-riverblue-primary hover:text-riverblue-deep underline"
            >
              What counts as eligible?
            </button>
          </div>
          <p className="text-xs text-gray-500">Contact support to review eligibility.</p>
        </div>

        {/* Connection Details Modal */}
        {selectedClient && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeModal}>
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Connection Details</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-3 text-sm text-gray-700 mb-6">
                <p>• Read-only OAuth</p>
                <p>• Disconnect anytime</p>
                <p>• No CRM write-back</p>
              </div>
              <button
                onClick={closeModal}
                className="w-full px-4 py-2 bg-riverblue-primary text-white rounded-lg hover:bg-riverblue-deep transition-colors text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* White-Label Eligibility Modal */}
        {showEligibilityModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowEligibilityModal(false)}>
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">White-Label Eligibility</h3>
                <button
                  onClick={() => setShowEligibilityModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-3 text-sm text-gray-700 mb-6">
                <p>
                  White-label eligibility is unlocked when you have <strong>1–3 paying clients</strong> actively using RiverBlue.
                </p>
                <p>
                  A paying client is one that has:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Completed initial setup</li>
                  <li>Active CRM connection</li>
                  <li>Regular data sync activity</li>
                </ul>
                <p className="text-xs text-gray-500 mt-4">
                  Contact support to review your eligibility status and discuss white-label options.
                </p>
              </div>
              <button
                onClick={() => setShowEligibilityModal(false)}
                className="w-full px-4 py-2 bg-riverblue-primary text-white rounded-lg hover:bg-riverblue-deep transition-colors text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
